import {useMutation, UseMutationResult, useQuery, UseQueryResult} from '@tanstack/react-query';
import {camelizeKeys, decamelize} from 'humps';
import {z as r} from 'zod';

import {useLocale} from '../../provider/locale/locale-hook';
import {useSnackbar} from '../../provider/snackbar/snackbar-hook';
import {TableSorterHookReturnType, useTableSorter} from '../../util/antd/table/table-sorter-hook';
import {fetchAndDeserialize, postAndDeserialize, serialize} from '../../util/api-adapter';
import {ApiError} from '../../util/error';
import {dropFetchXCache, FetchMethodEnum, fetchNoContent, fetchX} from '../../util/fetch';
import {usePagination} from '../../util/pagination-hook/pagination-hook';
import {PaginationType} from '../../util/pagination-hook/pagination-hook-type';
import {idNumberSchema, IdNumberType} from '../../util/type';
import {objectToUrlParameters} from '../../util/url';
import {
    companyAutoReplySuggestionsUrl,
    getCsrfHeaders,
    mainApiHeaders,
    responseTemplatesSuggestionsUrl,
    rootApiUrl,
    storeAutoReplySuggestionsUrl,
} from '../api/api-const';
import {generateResponseSchema, PaginatedResponseType, RequestOptionsType} from '../api/api-type';

const responseTemplateGroupSchema = r.object({
    id: r.number(),
    title: r.string(),
});

const responseTemplateGroupListSchema = r.array(responseTemplateGroupSchema);

export type ResponseTemplateGroupType = r.infer<typeof responseTemplateGroupSchema>;

const responseTemplateDataSchema = r.object({
    folder: responseTemplateGroupSchema.nullable(),
    id: r.number(),
    dateCreation: r.string(),
    isPrivate: r.boolean(),
    message: r.string(),
    title: r.string(),
    user: r.object({
        id: r.number(),
        email: r.string(),
    }),
});

const responseTemplatesListDataSchema = generateResponseSchema(responseTemplateDataSchema);

export type ResponseTemplateDataType = r.infer<typeof responseTemplateDataSchema>;

const autoReplyBalanceDataSchema = r.object({
    balance: r.number(),
    email: r.string(),
    userId: r.number(),
});

const autoReplyFromUnlimitedSchema = r.object({
    details: autoReplyBalanceDataSchema.array(),
    unlimitedLicence: r.boolean(),
});

type AutoReplyBalanceDataType = r.infer<typeof autoReplyFromUnlimitedSchema>;

const timeBucketSchema = r.object({
    start: r.string(),
    end: r.string(),
});

type TimeBucketType = r.infer<typeof timeBucketSchema>;

export enum AutoReplyTypeEnum {
    NeuralNetwork = 'neuralNetwork',
    Template = 'template',
}

export enum ReviewsAttachmentsFilterEnum {
    withAttachments = 'with_attachments',
    withoutAttachments = 'without_attachments',
}

export enum KeywordOperatorEnum {
    contains = 'contains',
    notContains = 'not_contains',
}

const keywordRuleSchema = r.object({
    operator: r.string(),
    words: r.array(r.string()),
});

export type KeywordRuleType = r.infer<typeof keywordRuleSchema>;

export enum AutoReplyRuleTypeEnum {
    Company = 'company',
    Store = 'store',
}

const autoReplyToReviewItemSchema = r.object({
    brandCatalogCompany: r.number().optional(),
    catalogs: r.array(idNumberSchema).optional(),
    companies: r.array(idNumberSchema).optional(),
    description: r.string().nullish(),
    enabled: r.boolean().optional(),
    enabledForDates: r.array(r.string()).nullable(),
    enabledFrom: r.string().nullish(),
    enabledTo: r.string().nullish(),
    id: r.number(),
    isDeleted: r.boolean().optional(),
    isTest: r.boolean().optional(),
    mode: r.string(),
    name: r.string(),
    responseTemplates: r.array(idNumberSchema).optional(),
    reviewsRatings: r.array(r.number().nullable()).optional(),
    reviewsTimeBucket: timeBucketSchema.nullable().optional(),
    reviewsWeekdaysIndexes: r.array(r.number()).nullable().optional(),
    reviewsWithBody: r.boolean().nullable().optional(),
    reviewsAttachmentsFilter: r.string().nullish(),
    reviewsKeywordRules: r.array(keywordRuleSchema).optional(),
    reviewsAutoTagIds: r.array(r.number()).nullish(),
    ruleType: r.nativeEnum(AutoReplyRuleTypeEnum).optional(),
    templateSelectionStrategy: r.string().optional(),
    templateTimeZone: r.string().optional(),
    replyType: r.nativeEnum(AutoReplyTypeEnum).optional(),
});

const autoReplyToReviewResponseSchema = generateResponseSchema(autoReplyToReviewItemSchema);

export type AutoReplyToReviewItemType = r.infer<typeof autoReplyToReviewItemSchema>;

export type AutoReplyDataErrorType = {
    selectTemplates: boolean;
    datePeriod: boolean;
    dateStart: boolean;
};

export enum AutoReplyErrorEnum {
    feedbackSource = 'feedbackSource',
    selectTemplates = 'selectTemplates',
    datePeriod = 'datePeriod',
    dateStart = 'dateStart',
}

export type AutoReplyToReviewBodyType = {
    brandCatalogCompany?: number;
    catalogs?: Array<IdNumberType | number> | null;
    description: string;
    enabled: boolean;
    enabledForDates: Array<string> | null;
    enabledFrom: string | null;
    enabledTo: string | null;
    id: number;
    isDeleted: boolean;
    isTest: boolean;
    mode: string;
    name: string;
    responseTemplates?: Array<number>;
    reviewsRatings: Array<number>;
    reviewsTimeBucket: TimeBucketType | null;
    reviewsWeekdaysIndexes: Array<number> | null;
    reviewsWithBody: boolean | null;
    reviewsAttachmentsFilter: string | null;
    reviewsKeywordRules: Array<KeywordRuleType>;
    reviewsAutoTagIds: Array<number> | null;
    uuid?: string;
    templateSelectionStrategy: string;
    templateTimeZone: string;
};

export type AutoReplyRuleRefType = {
    id: number;
    ruleType: AutoReplyRuleTypeEnum;
};

export type ChangeTemplateGroupBodyType = {
    ids: Array<string>;
    destination_folder_id?: number;
    without_folder?: boolean;
};

type ChangeTemplateBodyType = {
    ids: Array<string>;
    is_private: boolean;
};

type CreateGroupTemplatesType = {
    title: string;
};

type ChangeConditionAutoReplayBodyType = {
    enabled: boolean;
};

type ChangeEnabledAutoReplayBodyType = {
    ids: Array<string>;
    enabled: boolean;
};

type DeleteWithIdsType = {
    ids: Array<string> | number;
};

export const defaultResponseTemplateData: ResponseTemplateDataType = {
    folder: null,
    id: -1,
    isPrivate: false,
    message: '',
    title: '',
    user: {id: -1, email: ''},
    dateCreation: '',
};

export const defaultGroupTemplateData: ResponseTemplateGroupType = {
    id: -1,
    title: '',
};

export const defaultAutoReplyToReviewData: AutoReplyToReviewItemType = {
    catalogs: [],
    description: '',
    enabled: true,
    enabledForDates: [],
    enabledFrom: '',
    enabledTo: '',
    id: 0,
    isDeleted: false,
    isTest: false,
    mode: '',
    name: '',
    responseTemplates: [],
    reviewsRatings: [],
    reviewsTimeBucket: null,
    reviewsWeekdaysIndexes: [],
    reviewsWithBody: false,
    reviewsAttachmentsFilter: null,
    reviewsKeywordRules: [],
    reviewsAutoTagIds: null,
    ruleType: AutoReplyRuleTypeEnum.Company,
    templateSelectionStrategy: 'random',
    templateTimeZone: '',
    replyType: AutoReplyTypeEnum.NeuralNetwork,
};

export const defaultAutoReplyInfoErrors: AutoReplyDataErrorType = {
    selectTemplates: false,
    datePeriod: false,
    dateStart: false,
};

export const autoReplyFormErrorSchema = r.object({
    enabledFrom: r.array(r.string()).optional(),
});

export type AutoReplyFormErrorType = r.infer<typeof autoReplyFormErrorSchema>;

const responseTemplateGroupListUrl = '/v1/response_templates/folders/';

function fetchResponseTemplateGroupList(): Promise<Array<ResponseTemplateGroupType>> {
    return fetchAndDeserialize<Array<ResponseTemplateGroupType>>(
        responseTemplateGroupListUrl,
        responseTemplateGroupListSchema
    );
}

export function useResponseTemplateGroupList(): UseQueryResult<Array<ResponseTemplateGroupType>> {
    return useQuery([responseTemplateGroupListUrl], () => fetchResponseTemplateGroupList(), {
        refetchOnWindowFocus: false,
    });
}

function getResponseTemplatesUrl(
    option: RequestOptionsType,
    folderIdList: Array<string>,
    templateType: boolean | null,
    ordering?: string
) {
    return `/v1/response_templates/?${objectToUrlParameters({
        ...option,
        ordering,
        is_private: templateType,
    })}${folderIdList.map((id) => '&folder_id=' + id).join('')}`;
}

function fetchResponseTemplates(
    option: RequestOptionsType,
    folderIdList: Array<string>,
    templateType: boolean | null,
    ordering?: string
): Promise<PaginatedResponseType<ResponseTemplateDataType>> {
    return fetchAndDeserialize<PaginatedResponseType<ResponseTemplateDataType>>(
        getResponseTemplatesUrl(option, folderIdList, templateType, ordering),
        responseTemplatesListDataSchema
    );
}

export function useResponseTemplates(
    folderIdList: Array<string>,
    templateType: boolean | null,
    q?: string
): PaginationType &
    UseQueryResult<PaginatedResponseType<ResponseTemplateDataType>> &
    TableSorterHookReturnType<string> {
    const pagination = usePagination({dependencies: q ?? null, initialPageSize: 20});
    const {page, pageSize, onDataLoaded} = pagination;

    const responseTemplatesSort = useTableSorter();

    const query = useQuery(
        [
            getResponseTemplatesUrl(
                {count: pageSize, page, q},
                folderIdList,
                templateType,
                // eslint-disable-next-line no-undefined
                responseTemplatesSort.sorterQueryValue ? decamelize(responseTemplatesSort.sorterQueryValue) : undefined
            ),
        ],
        () =>
            fetchResponseTemplates(
                {count: pageSize, page, q},
                folderIdList,
                templateType,
                // eslint-disable-next-line no-undefined
                responseTemplatesSort.sorterQueryValue ? decamelize(responseTemplatesSort.sorterQueryValue) : undefined
            ),
        {
            onSuccess: (data) => onDataLoaded(data),
            refetchOnWindowFocus: false,
        }
    );

    return {...query, pagination, ...responseTemplatesSort};
}

function getResponseTemplateUrl(id: string) {
    return `/v1/response_templates/${id}/`;
}

function fetchResponseTemplateById(templateId: string): Promise<ResponseTemplateDataType> {
    return fetchAndDeserialize<ResponseTemplateDataType>(
        getResponseTemplateUrl(templateId),
        responseTemplateDataSchema
    );
}

export function useResponseTemplateById(templateId: string): UseQueryResult<ResponseTemplateDataType> {
    return useQuery([getResponseTemplateUrl(templateId)], () => fetchResponseTemplateById(templateId), {
        refetchOnWindowFocus: false,
    });
}

const autoRepliesBalanceUrl = '/v1/response_templates/suggestions/balance/';

function fetchAutoRepliesBalance(): Promise<Array<AutoReplyBalanceDataType>> {
    return fetchAndDeserialize<Array<AutoReplyBalanceDataType>>(autoRepliesBalanceUrl, autoReplyFromUnlimitedSchema);
}

export function useAutoRepliesBalance(): UseQueryResult<AutoReplyBalanceDataType> {
    return useQuery([autoRepliesBalanceUrl], () => fetchAutoRepliesBalance(), {
        refetchOnWindowFocus: false,
    });
}

function getAutoRepliesToReviewsUrl(option: RequestOptionsType) {
    return `/v1/response_templates/suggestions/?${objectToUrlParameters({
        is_deleted: false,
        ...option,
    })}`;
}

function fetchAutoRepliesToReviews(
    option: RequestOptionsType
): Promise<PaginatedResponseType<AutoReplyToReviewItemType>> {
    return fetchAndDeserialize<PaginatedResponseType<AutoReplyToReviewItemType>>(
        getAutoRepliesToReviewsUrl(option),
        autoReplyToReviewResponseSchema
    );
}

export function useAutoRepliesToReviews(
    option: RequestOptionsType
): UseQueryResult<PaginatedResponseType<AutoReplyToReviewItemType>> {
    const {count, page, q} = option;

    return useQuery([getAutoRepliesToReviewsUrl({count, page, q})], () => fetchAutoRepliesToReviews({count, page, q}), {
        refetchOnWindowFocus: false,
    });
}

function getAllResponseTemplateListUrl(reviewId?: number, catalogIds?: Array<number>) {
    return `/v1/response_templates/?${objectToUrlParameters({
        all: true,
        review_id: reviewId,
        catalog_ids: catalogIds,
    })}`;
}

function fetchAllResponseTemplateList(
    reviewId?: number,
    catalogIds?: Array<number>
): Promise<Array<ResponseTemplateDataType>> {
    return fetchAndDeserialize<Array<ResponseTemplateDataType>>(
        getAllResponseTemplateListUrl(reviewId, catalogIds),
        responseTemplatesListDataSchema,
        {fetchOptions: {skipCache: true}}
    );
}

export function useAllResponseTemplateList(
    reviewId?: number,
    catalogIds?: Array<number>
): UseQueryResult<Array<ResponseTemplateDataType>> {
    return useQuery(
        [getAllResponseTemplateListUrl(reviewId, catalogIds)],
        () => fetchAllResponseTemplateList(reviewId, catalogIds),
        {refetchOnWindowFocus: false}
    );
}

function getCompanyAutoReplyByIdUrl(autoReplyToReviewId: string | number) {
    return `${companyAutoReplySuggestionsUrl}${autoReplyToReviewId}/`;
}

function getStoreAutoReplyByIdUrl(autoReplyToReviewId: string | number) {
    return `${storeAutoReplySuggestionsUrl}${autoReplyToReviewId}/`;
}

function getAutoReplyByIdUrl(autoReplyToReviewId: string | number, ruleType: AutoReplyRuleTypeEnum) {
    return ruleType === AutoReplyRuleTypeEnum.Store
        ? getStoreAutoReplyByIdUrl(autoReplyToReviewId)
        : getCompanyAutoReplyByIdUrl(autoReplyToReviewId);
}

function fetchCompanyAutoReplyToReviewById(autoReplyToReviewId: string | number): Promise<AutoReplyToReviewItemType> {
    return fetchAndDeserialize<AutoReplyToReviewItemType>(
        getCompanyAutoReplyByIdUrl(autoReplyToReviewId),
        autoReplyToReviewItemSchema
    );
}

function fetchStoreAutoReplyToReviewById(autoReplyToReviewId: string | number): Promise<AutoReplyToReviewItemType> {
    return fetchAndDeserialize<AutoReplyToReviewItemType>(
        getStoreAutoReplyByIdUrl(autoReplyToReviewId),
        autoReplyToReviewItemSchema
    );
}

export function useAutoReplyToReviewById(
    autoReplyToReviewId: string | number,
    ruleType: AutoReplyRuleTypeEnum
): UseQueryResult<AutoReplyToReviewItemType> {
    return useQuery(
        [getAutoReplyByIdUrl(autoReplyToReviewId, ruleType)],
        () =>
            ruleType === AutoReplyRuleTypeEnum.Store
                ? fetchStoreAutoReplyToReviewById(autoReplyToReviewId)
                : fetchCompanyAutoReplyToReviewById(autoReplyToReviewId),
        {
            enabled: Boolean(autoReplyToReviewId),
            refetchOnWindowFocus: false,
        }
    );
}

export function fetchAutoReplayChangeCondition(
    id: string,
    data: ChangeConditionAutoReplayBodyType,
    ruleType: AutoReplyRuleTypeEnum
): Promise<AutoReplyToReviewItemType> {
    return fetchX<AutoReplyToReviewItemType>(`${rootApiUrl}${getAutoReplyByIdUrl(id, ruleType)}`, {
        method: FetchMethodEnum.patch,
        body: JSON.stringify(serialize(data)),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

function fetchCompanyAutoReplayUpdate(data: AutoReplyToReviewBodyType, id: string): Promise<AutoReplyToReviewItemType> {
    return fetchX<AutoReplyToReviewItemType>(`${rootApiUrl}${getCompanyAutoReplyByIdUrl(id)}`, {
        method: FetchMethodEnum.put,
        body: JSON.stringify(serialize(data)),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

function serializeStoreAutoReplyBody(data: AutoReplyToReviewBodyType): ReturnType<typeof serialize> {
    const {uuid: _uuid, ...storeData} = data;

    return serialize(storeData);
}

function fetchStoreAutoReplayUpdate(data: AutoReplyToReviewBodyType, id: string): Promise<AutoReplyToReviewItemType> {
    return fetchX<AutoReplyToReviewItemType>(`${rootApiUrl}${getStoreAutoReplyByIdUrl(id)}`, {
        method: FetchMethodEnum.put,
        body: JSON.stringify(serializeStoreAutoReplyBody(data)),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchAutoReplayUpdate(
    data: AutoReplyToReviewBodyType,
    id: string,
    ruleType: AutoReplyRuleTypeEnum
): Promise<AutoReplyToReviewItemType> {
    return ruleType === AutoReplyRuleTypeEnum.Store
        ? fetchStoreAutoReplayUpdate(data, id)
        : fetchCompanyAutoReplayUpdate(data, id);
}

function fetchCompanyAutoReplayCreate(data: AutoReplyToReviewBodyType): Promise<AutoReplyToReviewItemType> {
    return fetchX<AutoReplyToReviewItemType>(`${rootApiUrl}${companyAutoReplySuggestionsUrl}`, {
        method: FetchMethodEnum.post,
        body: JSON.stringify(serialize(data)),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

function fetchStoreAutoReplayCreate(data: AutoReplyToReviewBodyType): Promise<AutoReplyToReviewItemType> {
    return fetchX<AutoReplyToReviewItemType>(`${rootApiUrl}${storeAutoReplySuggestionsUrl}`, {
        method: FetchMethodEnum.post,
        body: JSON.stringify(serializeStoreAutoReplyBody(data)),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchAutoReplayCreate(
    data: AutoReplyToReviewBodyType,
    ruleType: AutoReplyRuleTypeEnum
): Promise<AutoReplyToReviewItemType> {
    return ruleType === AutoReplyRuleTypeEnum.Store
        ? fetchStoreAutoReplayCreate(data)
        : fetchCompanyAutoReplayCreate(data);
}

function fetchCompanyAutoReplayDelete(id: string): Promise<void> {
    return fetchNoContent(`${rootApiUrl}${getCompanyAutoReplyByIdUrl(id)}`, {
        method: FetchMethodEnum.delete,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

function fetchStoreAutoReplayDelete(id: string): Promise<void> {
    return fetchNoContent(`${rootApiUrl}${getStoreAutoReplyByIdUrl(id)}`, {
        method: FetchMethodEnum.delete,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchAutoReplayDelete(id: string, ruleType: AutoReplyRuleTypeEnum): Promise<void> {
    return ruleType === AutoReplyRuleTypeEnum.Store ? fetchStoreAutoReplayDelete(id) : fetchCompanyAutoReplayDelete(id);
}

function fetchCompanyAutoReplaysDelete(data: DeleteWithIdsType): Promise<AutoReplyToReviewItemType> {
    const parameters = objectToUrlParameters(data);

    return fetchX<AutoReplyToReviewItemType>(`${rootApiUrl}${companyAutoReplySuggestionsUrl}?${parameters}`, {
        method: FetchMethodEnum.delete,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

function fetchStoreAutoReplaysDelete(data: DeleteWithIdsType): Promise<AutoReplyToReviewItemType> {
    const parameters = objectToUrlParameters(data);

    return fetchX<AutoReplyToReviewItemType>(`${rootApiUrl}${storeAutoReplySuggestionsUrl}?${parameters}`, {
        method: FetchMethodEnum.delete,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export async function fetchAutoReplaysDelete(
    rules: Array<AutoReplyRuleRefType>
): Promise<AutoReplyToReviewItemType | void> {
    const companyIds = rules.filter((rule) => rule.ruleType === AutoReplyRuleTypeEnum.Company).map((rule) => rule.id);
    const storeIds = rules.filter((rule) => rule.ruleType === AutoReplyRuleTypeEnum.Store).map((rule) => rule.id);

    const requests: Array<Promise<AutoReplyToReviewItemType>> = [];

    if (companyIds.length > 0) {
        requests.push(fetchCompanyAutoReplaysDelete({ids: companyIds.map(String)}));
    }

    if (storeIds.length > 0) {
        requests.push(fetchStoreAutoReplaysDelete({ids: storeIds.map(String)}));
    }

    const results = await Promise.all(requests);

    return results[0];
}

const changeEnabledOkSchema = r.object({
    status: r.string(),
});

const changeEnabledConflictErrorSchema = r.object({
    status: r.string(),
    enabledIds: r.array(r.number()),
    conflictIds: r.array(r.number()),
});

type ChangeEnabledConflictType = r.infer<typeof changeEnabledConflictErrorSchema>;

function fetchCompanyChangeEnabledAutoReplay(
    data: ChangeEnabledAutoReplayBodyType
): Promise<r.infer<typeof changeEnabledOkSchema>> {
    return postAndDeserialize<r.infer<typeof changeEnabledOkSchema>>(
        `${responseTemplatesSuggestionsUrl}change_enabled/`,
        changeEnabledOkSchema,
        data
    );
}

function fetchStoreChangeEnabledAutoReplay(
    data: ChangeEnabledAutoReplayBodyType
): Promise<r.infer<typeof changeEnabledOkSchema>> {
    return postAndDeserialize<r.infer<typeof changeEnabledOkSchema>>(
        `${responseTemplatesSuggestionsUrl}stores/change_enabled/`,
        changeEnabledOkSchema,
        data
    );
}

function getConflictFromError(error: unknown): ChangeEnabledConflictType | null {
    if (!(error instanceof ApiError)) {
        return null;
    }

    const result = changeEnabledConflictErrorSchema.safeParse(camelizeKeys(error.jsonData));

    return result.success ? result.data : null;
}

async function fetchBulkChangeEnabledAutoReplay(rules: Array<AutoReplyRuleRefType>, enabled: boolean): Promise<void> {
    const companyIds = rules
        .filter((rule) => rule.ruleType === AutoReplyRuleTypeEnum.Company)
        .map((rule) => String(rule.id));
    const storeIds = rules
        .filter((rule) => rule.ruleType === AutoReplyRuleTypeEnum.Store)
        .map((rule) => String(rule.id));

    const requests: Array<Promise<r.infer<typeof changeEnabledOkSchema>>> = [];

    if (companyIds.length > 0) {
        requests.push(fetchCompanyChangeEnabledAutoReplay({ids: companyIds, enabled}));
    }

    if (storeIds.length > 0) {
        requests.push(fetchStoreChangeEnabledAutoReplay({ids: storeIds, enabled}));
    }

    await Promise.all(requests);
}

type ChangeEnabledAutoReplayMutationInputType = {
    enabled: boolean;
    rules: Array<AutoReplyRuleRefType>;
};

export function useChangeEnabledAutoReplyMutation(
    autoRepliesList: Array<AutoReplyToReviewItemType>
): UseMutationResult<void, unknown, ChangeEnabledAutoReplayMutationInputType> {
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();

    function onSuccess() {
        snackbar.success(getLocalizedString('AUTO_REPLIES__NOTIFICATION__SUCCESS_UPDATE'));
    }

    function onError(error: unknown) {
        const conflict = getConflictFromError(error);

        if (conflict) {
            const ruleNames = conflict.conflictIds
                .map((id) => autoRepliesList.find((rule) => rule.id === id)?.name ?? String(id))
                .join(', ');

            snackbar.error({
                message: conflict.status,
                description: getLocalizedString('AUTO_REPLIES__NOTIFICATION__ERROR_CONFLICT_IDS', {ruleNames}),
            });
        } else {
            snackbar.error(getLocalizedString('AUTO_REPLIES__NOTIFICATION__ERROR_UPDATE'));
        }
    }

    return useMutation(({rules, enabled}) => fetchBulkChangeEnabledAutoReplay(rules, enabled), {onSuccess, onError});
}

export function fetchTemplateCreate(data: ResponseTemplateDataType): Promise<ResponseTemplateDataType> {
    return fetchX<ResponseTemplateDataType>(`${rootApiUrl}/v1/response_templates/`, {
        method: FetchMethodEnum.post,
        body: JSON.stringify(serialize(data)),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchTemplateUpdate(data: ResponseTemplateDataType, id: number): Promise<ResponseTemplateDataType> {
    return fetchX<ResponseTemplateDataType>(`${rootApiUrl}/v1/response_templates/${id}/`, {
        method: FetchMethodEnum.put,
        body: JSON.stringify(serialize(data)),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchTemplateDelete(data: DeleteWithIdsType): Promise<ResponseTemplateDataType> {
    const parameters = objectToUrlParameters(data);

    return fetchX<ResponseTemplateDataType>(`${rootApiUrl}/v1/response_templates/?${parameters}`, {
        method: FetchMethodEnum.delete,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchTemplateGroupCreate(data: CreateGroupTemplatesType): Promise<ResponseTemplateGroupType> {
    return fetchX<ResponseTemplateGroupType>(rootApiUrl + '/v1/response_templates/folders/', {
        method: FetchMethodEnum.post,
        body: JSON.stringify(serialize(data)),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchTemplateGroupUpdate(
    data: ResponseTemplateGroupType,
    id: number
): Promise<ResponseTemplateGroupType> {
    return fetchX<ResponseTemplateGroupType>(`${rootApiUrl}/v1/response_templates/folders/${id}/`, {
        method: FetchMethodEnum.put,
        body: JSON.stringify(serialize(data)),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchTemplateGroupDelete(data: DeleteWithIdsType): Promise<void> {
    dropFetchXCache([`${rootApiUrl}/v1/response_templates/folders/ - ${JSON.stringify('[empty]')}`]);
    const parameters = objectToUrlParameters(data);

    return fetchNoContent(`${rootApiUrl}/v1/response_templates/folders/?${parameters}`, {
        method: FetchMethodEnum.delete,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchTemplateChangeType(data: ChangeTemplateBodyType): Promise<ResponseTemplateDataType> {
    return fetchX<ResponseTemplateDataType>(`${rootApiUrl}/v1/response_templates/change_templates_type/`, {
        method: FetchMethodEnum.post,
        body: JSON.stringify(serialize(data)),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}

export function fetchTemplateChangeGroup(data: ChangeTemplateGroupBodyType): Promise<ResponseTemplateDataType> {
    return fetchX<ResponseTemplateDataType>(`${rootApiUrl}/v1/response_templates/change_templates_folder/`, {
        method: FetchMethodEnum.post,
        body: JSON.stringify(serialize(data)),
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
    });
}
